// handlers/buildFeedbackMinMaxQuestions.js

import { questionTypeQuestion } from "../constants/questionTypes";

/**
 * questions: the brief_response.questions payload (with .factors[] etc.) OR null
 * min_followup / max_followup: numbers from the brief
 * questionSequenceMap: the stored_response.questionSequenceMap object
 */
export function buildFeedbackMinMaxQuestions(
  questions,
  min_followup,
  max_followup,
  questionSequenceMap
) {
  const MinQuestions = [];
  const MaxQuestions = [];

  const MinFactorSet = new Set(); // keep one min per factor
  const MaxFactorSet = new Set(); // keep one max per factor

  // Basic guards
  if (!questionSequenceMap || (!min_followup && !max_followup)) {
    return [];
  }

  // Helper: get factor metadata (title, base question text) from brief_response.questions
  const getFactorMeta = (factorIndex, quesListNo) => {
    if (!questions || !Array.isArray(questions.factors)) {
      return {};
    }

    const factor = questions.factors[factorIndex];
    if (!factor) return {};

    const qIdx = (quesListNo ?? 1) - 1;
    const qMeta = Array.isArray(factor.questions)
      ? factor.questions[qIdx] || factor.questions[0]
      : null;

    return {
      factorTitle: factor.title,
      baseQuestionText: qMeta?.q,
      reverse: qMeta?.reverse ?? false,
      questionId: qMeta?.id,
    };
  };

  // 1) Collect all scored questions from the sequence map
  const scored = Object.values(questionSequenceMap)
    .map((step) => {

        // we only care about real survey questions with a factor
      const isQuestionStep =
        step?.quesType === questionTypeQuestion || step?.quesType === "question";

      if (!isQuestionStep) return null;
      if (typeof step.factor === "undefined" || step.factor === null) return null;

      // response may be { response: X } or just a scalar
      const rawScore =
        step?.response && typeof step.response === "object"
          ? step.response.response
          : step?.response;

      const score = Number(rawScore);
      if (!Number.isFinite(score)) return null;

      const factorIndex = step.factor;
      const { factorTitle, baseQuestionText, reverse, questionId } =
        getFactorMeta(factorIndex, step.quesListNo);

      const questionText =
        step.question_text ||
        baseQuestionText ||
        step.question ||
        "";

      return {
        id: step.id,
        factor: factorIndex,
        factorTitle,
        reverse,
        quesListNo: step.quesListNo,
        questionId,
        question: questionText,
        response: score,
      };
    })
    .filter(Boolean);

  if (!scored.length) {
    return [];
  }

  // 2) Sort once ascending by score
  const ascending = [...scored].sort((a, b) => a.response - b.response);

  // 3) Build MIN list (lowest scores)
  if (min_followup > 0) {
    for (const item of ascending) {
      if (MinFactorSet.has(item.factor)) continue;
      MinFactorSet.add(item.factor);

      MinQuestions.push({
        ...item,
        type: "min",
        feedback: "",
      });

      if (MinQuestions.length >= min_followup) break;
    }
  }

  // 4) Build MAX list (highest scores)
  if (max_followup > 0) {
    for (let i = ascending.length - 1; i >= 0; i--) {
      const item = ascending[i];
      if (MaxFactorSet.has(item.factor)) continue;
      MaxFactorSet.add(item.factor);

      MaxQuestions.push({
        ...item,
        type: "max",
        feedback: "",
      });

      if (MaxQuestions.length >= max_followup) break;
    }
  }

  return [
    ...MinQuestions.slice(0, min_followup),
    ...MaxQuestions.slice(0, max_followup),
  ];
}
